import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { WhatsAppDeviceService } from '@shared/services/whatsapp-device.service';
import * as QRCode from 'qrcode';

export interface WhatsAppQrConnectedPayload {
  instanceId: string;
  accountId: string;
  phoneNumber: string | null;
}

@Component({
  selector: 'app-whatsapp-qr-modal',
  templateUrl: './whatsapp-qr-modal.component.html',
  styleUrl: './whatsapp-qr-modal.component.scss',
})
export class WhatsAppQrModalComponent implements OnInit, OnDestroy {
  private _open = false;
  @Input() set open(value: boolean) {
    const prev = this._open;
    this._open = value;
    if (value && !prev) {
      this.sessionId = null;
      this.startSession();
    }
    if (!value && prev) {
      this.onClose();
    }
  }
  get open(): boolean {
    return this._open;
  }
  @Input() accountId: string | undefined;

  @Output() connected = new EventEmitter<WhatsAppQrConnectedPayload>();
  @Output() closed = new EventEmitter<void>();

  state: 'loading' | 'qr' | 'connected' | 'expired' | 'error' = 'loading';
  qrDataUrl: string | null = null;
  errorMessage: string | null = null;
  sessionId: string | null = null;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly pollIntervalMs = 2000;

  constructor(
    private deviceService: WhatsAppDeviceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.sessionId) {
      this.deviceService.cancelLinkSession(this.sessionId).subscribe({
        next: () => {},
        error: () => {},
      });
    }
  }

  startSession(): void {
    if (this.sessionId) return;
    this.state = 'loading';
    this.errorMessage = null;
    this.qrDataUrl = null;
    this.cdr.markForCheck();

    const params = this.accountId ? { accountId: this.accountId } : undefined;
    this.deviceService.createLinkSession(params).subscribe({
      next: (data) => {
        this.sessionId = data.sessionId;
        this.startPolling();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.state = 'error';
        this.errorMessage = err?.error?.message ?? err?.message ?? 'Failed to start session';
        this.cdr.markForCheck();
      },
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollIntervalId = setInterval(() => this.poll(), this.pollIntervalMs);
    this.poll();
  }

  private stopPolling(): void {
    if (this.pollIntervalId != null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private poll(): void {
    if (!this.sessionId) return;
    this.deviceService.getLinkSessionStatus(this.sessionId).subscribe({
      next: (data) => {
        if (data.status === 'CONNECTED' && data.accountId) {
          this.stopPolling();
          this.state = 'connected';
          this.cdr.markForCheck();
          this.fetchInstanceAndEmit(data.accountId);
          return;
        }
        if (data.status === 'EXPIRED') {
          this.stopPolling();
          this.state = 'expired';
          this.cdr.markForCheck();
          return;
        }
        if (data.status === 'ERROR') {
          this.stopPolling();
          this.state = 'error';
          this.errorMessage = data.error ?? 'Connection error';
          this.cdr.markForCheck();
          return;
        }
        const qr = data.qrCodeData;
        if (qr && qr.length > 20 && !qr.startsWith('wa_link:') && qr !== 'pending') {
          this.state = 'qr';
          QRCode.toDataURL(qr, { width: 256, margin: 1 }).then((dataUrl: string) => {
            this.qrDataUrl = dataUrl;
            this.cdr.markForCheck();
          }).catch(() => {
            this.qrDataUrl = null;
            this.cdr.markForCheck();
          });
        } else if (this.state === 'loading') {
          this.state = 'qr';
          this.cdr.markForCheck();
        }
      },
      error: () => {},
    });
  }

  private fetchInstanceAndEmit(accountId: string): void {
    this.deviceService.getInstanceDetail(accountId).subscribe({
      next: (instance) => {
        this.sessionId = null;
        this.connected.emit({
          instanceId: instance.instanceId,
          accountId: instance.id,
          phoneNumber: instance.phone ?? null,
        });
        this.closed.emit();
      },
      error: () => {
        this.sessionId = null;
        this.connected.emit({
          instanceId: '',
          accountId,
          phoneNumber: null,
        });
        this.closed.emit();
      },
    });
  }

  onClose(): void {
    this.stopPolling();
    if (this.sessionId) {
      this.deviceService.cancelLinkSession(this.sessionId).subscribe({
        next: () => {},
        error: () => {},
        complete: () => {
          this.sessionId = null;
          this.closed.emit();
        },
      });
    } else {
      this.closed.emit();
    }
  }

  retry(): void {
    this.sessionId = null;
    this.state = 'loading';
    this.errorMessage = null;
    this.qrDataUrl = null;
    this.startSession();
  }
}
